/**
 * Per-methode testverslag: hoe de redactie een storting en opname met die
 * specifieke betaalmethode doorloopt. Eén record per methodepagina, gerenderd
 * via één gedeelde component, zodat geen sectie dubbel op een pagina staat.
 * Cijfers (tijden, aantallen) komen uit src/lib/testdata.ts, niet uit deze tekst.
 */
export type MethodWalkthrough = {
  /** Testopzet in de eigen woorden van de tester. */
  setup: string;
  deposit: string[];
  withdrawal: string[];
  /** Waar verificatie in de flow opdook. */
  verification: string;
  /** Fouten en vertragingen die we tegenkwamen of die spelers vaak maken. */
  pitfalls: string[];
  /** Voor wie de methode het best past. */
  bestFor: string;
  /** Eerlijk alternatief en waarom. */
  alternative: { slug: string; name: string; why: string };
};

export const METHOD_WALKTHROUGHS: Record<string, MethodWalkthrough> = {
  ideal: {
    setup:
      "We testten iDEAL met betaalrekeningen bij ING, Rabobank en ABN AMRO, zowel op een iPhone met de bank-app als op desktop via de QR-code. Elk account was nieuw, zodat we ook de eerste-storting-flow zagen.",
    deposit: [
      "In de kassa iDEAL kiezen en het bedrag invullen; de meeste aanbieders vragen vanaf €10.",
      "Op mobiel opent direct de bank-app, op desktop scan je de QR-code met je telefoon.",
      "Bevestigen met vingerafdruk, Face ID of pincode in de bank-app.",
      "Terug naar het casino: het saldo stond in alle tests binnen enkele seconden klaar.",
    ],
    withdrawal: [
      "Opname aanvragen via de kassa; de uitbetaling gaat naar het IBAN waarmee je met iDEAL stortte.",
      "Na interne goedkeuring volgt een directe bankoverboeking; bij snelle aanbieders via instant payments.",
      "We noteren de tijd tot het bedrag zichtbaar is in de bank-app, niet tot de status 'verwerkt' in het casino.",
    ],
    verification:
      "Omdat iDEAL aan je eigen bankrekening hangt, is je IBAN meteen geverifieerd. Een paar aanbieders vroegen bij de eerste opname alsnog om een identiteitsbewijs.",
    pitfalls: [
      "Een rekening op naam van een partner werkt niet: de naam moet gelijk zijn aan je casino-account.",
      "Rond 23:00–01:00 verwerken sommige banken onderhoud, waardoor de bank-app traag opent.",
    ],
    bestFor: "Spelers met een Nederlandse bankrekening die zonder extra account direct willen storten en terug naar hun eigen rekening willen opnemen.",
    alternative: { slug: "trustly", name: "Trustly", why: "werkt ook met buitenlandse banken en betaalt eveneens rechtstreeks terug naar je rekening." },
  },
  sofort: {
    setup:
      "Sofort (onderdeel van Klarna) testten we met een Nederlandse en een Duitse bankrekening op desktop en iPhone. Sofort vraagt inloggen met je internetbankiergegevens, dus we keken extra naar de stappen en de foutgevoeligheid.",
    deposit: [
      "Sofort kiezen in de kassa en je bankland selecteren.",
      "Inloggen met je bankgegevens in het Sofort-scherm.",
      "Bevestigen met een TAN-code of de bank-app.",
      "Na bevestiging volgt een succesmelding; het saldo verscheen in onze tests binnen een halve minuut.",
    ],
    withdrawal: [
      "Sofort kan zelf niet uitbetalen; het casino maakt de winst over naar het IBAN van de storting.",
      "We vroegen telkens het restsaldo op en maten tot bijschrijving op de bankrekening.",
    ],
    verification:
      "De bankrekening is na de eerste Sofort-storting gekoppeld. Bij een opname boven het bonusbedrag vroegen enkele casino's een bankafschrift.",
    pitfalls: [
      "Bij banken met een extra 2FA-stap duurt het inloggen langer dan bij iDEAL.",
      "Sluit het Sofort-venster niet voor de succesmelding, anders blijft de storting soms hangen.",
    ],
    bestFor: "Spelers met een Duitse of Oostenrijkse bankrekening, of wie iDEAL niet in de kassa ziet staan.",
    alternative: { slug: "ideal", name: "iDEAL", why: "is voor Nederlandse rekeningen sneller en vraagt geen inlog met bankgegevens in een extern scherm." },
  },
  "apple-pay": {
    setup:
      "Apple Pay testten we op een iPhone 15 en een iPad met een Visa- en een Mastercard-debitkaart in Wallet. We keken vooral naar het aantal tikken tot een speelbaar saldo en naar de opnameroute.",
    deposit: [
      "In de kassa Apple Pay kiezen; de knop verschijnt alleen in Safari of de casino-app.",
      "Kaart kiezen in het Apple Pay-venster en bevestigen met Face ID of Touch ID.",
      "De storting staat er direct op; in onze tests binnen enkele seconden.",
    ],
    withdrawal: [
      "Opnemen via Apple Pay kon bij geen enkele aanbieder.",
      "Het casino stelt bankoverschrijving voor; we vulden ons IBAN in en volgden die overboeking.",
    ],
    verification:
      "Omdat Apple Pay een tokenkaart gebruikt, vroegen de meeste casino's bij de eerste opname alsnog om identiteit en IBAN-bewijs.",
    pitfalls: [
      "Op Android of in Chrome zie je de Apple Pay-knop niet.",
      "Met een creditcard in Wallet kan je uitgever extra kosten rekenen; een debitkaart is goedkoper.",
    ],
    bestFor: "iPhone-gebruikers die snel willen storten en het geen probleem vinden dat opnames via de bank lopen.",
    alternative: { slug: "ideal", name: "iDEAL", why: "is even snel op mobiel en laat je ook op dezelfde rekening uitbetalen." },
  },
  "google-pay": {
    setup:
      "Google Pay testten we op een Pixel 8 en een Samsung Galaxy met een gekoppelde debitkaart, in Chrome en in de casino-app.",
    deposit: [
      "Google Pay kiezen in de kassa; de knop staat alleen op Android of in Chrome.",
      "De kaart kiezen in Google Wallet en ontgrendelen met vingerafdruk of pincode.",
      "Saldo staat er direct op, meestal binnen tien seconden.",
    ],
    withdrawal: [
      "Google Pay ondersteunt geen opnames; winst gaat per bankoverschrijving naar je IBAN.",
      "We maten de tijd van opnameverzoek tot bijschrijving op de rekening die we invulden.",
    ],
    verification:
      "Bij de eerste opname vroegen alle geteste casino's om ID en een bewijs van je IBAN, omdat de kaart achter Google Pay verborgen blijft.",
    pitfalls: [
      "Niet elke bank ondersteunt Google Pay; controleer dit in je bank-app.",
      "Een virtuele kaart wordt niet door elk casino geaccepteerd.",
    ],
    bestFor: "Android-gebruikers die met één vingerafdruk willen storten.",
    alternative: { slug: "apple-pay", name: "Apple Pay", why: "is het iOS-equivalent; wie wil uitbetalen naar dezelfde methode kiest beter een e-wallet." },
  },
  neteller: {
    setup:
      "We gebruikten een volledig geverifieerd Neteller-account (Silver-niveau) dat we vooraf met iDEAL hadden opgewaardeerd. Zo testten we storten én opnemen naar de wallet.",
    deposit: [
      "Neteller kiezen en het e-mailadres van je wallet invullen.",
      "Inloggen bij Neteller en bevestigen met de app of een 2FA-code.",
      "Het saldo verscheen binnen ongeveer tien seconden.",
    ],
    withdrawal: [
      "Opname aanvragen naar dezelfde Neteller-wallet.",
      "Na goedkeuring stond het geld in onze tests binnen enkele uren in de wallet.",
      "Daarna eventueel doorboeken naar je bank; Neteller kan daarvoor kosten rekenen.",
    ],
    verification:
      "Neteller vraagt zelf al KYC. Bij de casino's zelf was extra verificatie daardoor minder vaak nodig dan bij kaartbetalingen.",
    pitfalls: [
      "Let op wisselkosten als je wallet niet in euro staat.",
      "Een Neteller-storting telt bij sommige aanbieders niet mee voor een welkomstbonus; lees de voorwaarden.",
    ],
    bestFor: "Spelers die casinogeld gescheiden van hun bankrekening willen houden en snel naar een wallet willen opnemen.",
    alternative: { slug: "skrill", name: "Skrill", why: "werkt vrijwel hetzelfde en is van hetzelfde moederbedrijf; vergelijk vooral de opwaardeer- en opnamekosten." },
  },
  hipay: {
    setup:
      "HiPay is geen wallet voor spelers maar een betaalverwerker achter de kassa. We testten de kaart- en bankroutes die via HiPay lopen bij aanbieders die HiPay als naam in de kassa tonen.",
    deposit: [
      "HiPay kiezen; je komt in een betaalscherm voor kaart of bankbetaling.",
      "Gegevens invullen en bevestigen met 3D Secure of de bank-app.",
      "Het saldo verscheen meestal binnen een halve minuut.",
    ],
    withdrawal: [
      "Opnemen gaat terug naar de kaart of rekening van de storting.",
      "We maten tot bijschrijving; die hangt meer af van het casino dan van HiPay zelf.",
    ],
    verification:
      "Omdat HiPay een verwerker is, liep verificatie volledig via het casino. Kaartstortingen vroegen vaker om een foto van de kaart (middelste cijfers afgedekt).",
    pitfalls: [
      "HiPay staat soms op je afschrift in plaats van de casinonaam; dat is normaal.",
      "Een bank kan een eerste betaling naar een buitenlandse verwerker extra controleren.",
    ],
    bestFor: "Spelers die gewoon met hun pas of bank willen betalen bij een casino dat HiPay als verwerker gebruikt.",
    alternative: { slug: "ideal", name: "iDEAL", why: "geeft dezelfde bankroute zonder tussenscherm en is in Nederland breder beschikbaar." },
  },
  trustly: {
    setup:
      "Trustly testten we met Nederlandse rekeningen bij ING en bunq en met een Zweedse testrekening. Trustly werkt zonder eigen account: je logt in bij je bank via Trustly.",
    deposit: [
      "Trustly kiezen en je bank selecteren.",
      "Inloggen via de bank-app of met je bankgegevens in het Trustly-scherm.",
      "De rekening kiezen en bevestigen; het saldo stond er binnen seconden.",
    ],
    withdrawal: [
      "Opnemen gaat terug naar de rekening die Trustly heeft gekoppeld.",
      "Bij snelle aanbieders kwam het geld via instant payments binnen het uur binnen.",
    ],
    verification:
      "Trustly geeft naam en IBAN door aan het casino, waardoor de bankcontrole vaak al is afgevinkt. Een ID-check bleef bij sommige aanbieders nodig.",
    pitfalls: [
      "Het eerste inloggen vraagt meer stappen dan iDEAL.",
      "Trustly koppelt soms automatisch je hoofdrekening; kies bewust de juiste rekening.",
    ],
    bestFor: "Spelers die snel terug naar de bank willen opnemen, ook met een niet-Nederlandse rekening.",
    alternative: { slug: "ideal", name: "iDEAL", why: "voelt voor Nederlandse spelers vertrouwder en vraagt minder stappen bij de eerste storting." },
  },
  paysafecard: {
    setup:
      "We kochten paysafecard-tegoeden van €10, €25 en €50 bij een supermarkt en online, en stortten daarmee op desktop en mobiel. Daarnaast testten we een my paysafecard-account voor opnames.",
    deposit: [
      "paysafecard kiezen en het bedrag invullen.",
      "De 16-cijferige PIN van de voucher invoeren; meerdere PIN's combineren kan tot het voucherbedrag.",
      "Bevestigen; het saldo verscheen binnen een paar seconden.",
    ],
    withdrawal: [
      "Met een losse voucher kun je niet opnemen; het casino vraagt een bankoverschrijving.",
      "Met een my paysafecard-account kon het bij enkele aanbieders wel, maar dat was eerder uitzondering dan regel.",
    ],
    verification:
      "Storten vraagt geen bankgegevens, maar bij de eerste opname moesten we altijd ID en IBAN aanleveren.",
    pitfalls: [
      "Een restbedrag op een voucher blijft staan; noteer je PIN tot het tegoed op is.",
      "Na een jaar zonder gebruik kan paysafecard servicekosten rekenen.",
    ],
    bestFor: "Spelers die vooraf een vast budget willen vastzetten en niets met hun bank willen delen bij het storten.",
    alternative: { slug: "neosurf", name: "Neosurf", why: "werkt ook met vooruitbetaalde vouchers en is bij sommige aanbieders breder beschikbaar." },
  },
  paypal: {
    setup:
      "PayPal testten we met een geverifieerd Nederlands account met gekoppelde bankrekening, op desktop en via de PayPal-app.",
    deposit: [
      "PayPal kiezen in de kassa; er opent een PayPal-venster.",
      "Inloggen of bevestigen in de PayPal-app.",
      "Het saldo stond er binnen enkele seconden.",
    ],
    withdrawal: [
      "Opnemen naar hetzelfde PayPal-account.",
      "Het geld stond in onze tests binnen enkele uren in PayPal, daarna gratis door te boeken naar de bank.",
    ],
    verification:
      "PayPal-accounts zijn al geverifieerd, maar casino's controleren of de naam overeenkomt met je casino-account.",
    pitfalls: [
      "PayPal werkt alleen bij een deel van de Nederlandse vergunninghouders.",
      "Een zakelijk PayPal-account wordt vaak geweigerd.",
    ],
    bestFor: "Spelers die al PayPal gebruiken en een extra buffer tussen casino en bank willen.",
    alternative: { slug: "trustly", name: "Trustly", why: "is breder beschikbaar en betaalt even snel rechtstreeks naar je bank." },
  },
  skrill: {
    setup:
      "We gebruikten een geverifieerd Skrill-account in euro, opgewaardeerd via bankoverschrijving, en testten storten en opnemen op desktop en Android.",
    deposit: [
      "Skrill kiezen en je Skrill-e-mailadres invullen.",
      "Inloggen en bevestigen met 2FA.",
      "Saldo binnen ongeveer tien seconden zichtbaar.",
    ],
    withdrawal: [
      "Opname terug naar dezelfde Skrill-wallet.",
      "Na goedkeuring stond het geld er in onze tests binnen enkele uren.",
    ],
    verification:
      "Skrill vereist eigen KYC; de casino's vroegen daarnaast soms om een screenshot van je Skrill-profiel.",
    pitfalls: [
      "Opwaarderen met een kaart kost bij Skrill vaak extra.",
      "Sommige aanbieders sluiten Skrill-stortingen uit van bonussen.",
    ],
    bestFor: "Spelers die meerdere casino's vanuit één wallet beheren en snel naar die wallet willen opnemen.",
    alternative: { slug: "neteller", name: "Neteller", why: "is vrijwel identiek en soms goedkoper bij opwaarderen." },
  },
  revolut: {
    setup:
      "Revolut testten we op twee manieren: met de Revolut-debitkaart (Visa en Mastercard) en, waar beschikbaar, via Revolut Pay. Beide op iPhone en Android.",
    deposit: [
      "In de kassa de kaartbetaling of Revolut Pay kiezen.",
      "Bij een kaart: gegevens invullen en bevestigen in de Revolut-app (3D Secure).",
      "Het saldo stond er direct op.",
    ],
    withdrawal: [
      "Opnemen naar je Revolut-IBAN (beginnend met LT of NL) via bankoverschrijving.",
      "We maten tot de pushmelding van Revolut dat het geld binnen was.",
    ],
    verification:
      "Omdat Revolut een eigen IBAN geeft, accepteerden de meeste aanbieders dat als bankbewijs; sommigen vroegen een afschrift uit de app.",
    pitfalls: [
      "Revolut kan gokbetalingen blokkeren als je dat in de app hebt uitgezet.",
      "Een Litouws IBAN wordt niet door elk casino geaccepteerd voor opnames.",
    ],
    bestFor: "Spelers die Revolut als dagelijkse rekening gebruiken en alles in één app willen zien.",
    alternative: { slug: "ideal", name: "iDEAL", why: "werkt ook met een Revolut NL-IBAN en is bij alle aanbieders beschikbaar." },
  },
  visa: {
    setup:
      "We testten met Visa-debitkaarten van ING en ABN AMRO, een Visa-creditcard en een prepaidkaart, zodat het verschil tussen directe en uitgestelde afschrijving zichtbaar werd.",
    deposit: [
      "Kaartbetaling kiezen en kaartnummer, vervaldatum en CVC invullen.",
      "Bevestigen met 3D Secure in de bank-app.",
      "Het saldo verscheen meestal binnen tien seconden.",
    ],
    withdrawal: [
      "Opnemen naar dezelfde Visa-debitkaart waar het casino dat ondersteunt (Visa Direct).",
      "Waar dat niet kon, ging de winst per overschrijving naar je IBAN.",
    ],
    verification:
      "Bij de eerste opname vroegen de meeste casino's een foto van de kaart met afgedekte middelste cijfers.",
    pitfalls: [
      "Een prepaidkaart kon wel storten maar niet altijd opnemen.",
      "Sommige banken blokkeren de eerste betaling aan een casino; keur die goed in de app.",
    ],
    bestFor: "Spelers die gewoon met hun bankpas willen betalen zonder extra account.",
    alternative: { slug: "mastercard", name: "Mastercard", why: "werkt op dezelfde manier; welke je kiest hangt vooral af van de pas die je al hebt." },
  },
  mastercard: {
    setup:
      "Mastercard testten we met debitkaarten van Rabobank, bunq en Revolut. We keken naar de 3D Secure-flow en naar opnames via Mastercard Send.",
    deposit: [
      "Kaartbetaling kiezen en de debitkaartgegevens invullen.",
      "Bevestigen met Mastercard Identity Check in de bank-app.",
      "Het saldo was binnen ongeveer tien seconden speelbaar.",
    ],
    withdrawal: [
      "Opnemen naar dezelfde debitkaart waar het casino Mastercard Send gebruikt.",
      "Anders volgt een bankoverschrijving naar je IBAN; we maten beide routes tot bijschrijving.",
    ],
    verification:
      "Net als bij Visa vroegen casino's bij de eerste opname een kaartfoto met afgedekte cijfers.",
    pitfalls: [
      "Met een Mastercard-creditcard kan de uitgever kosten voor contantopname rekenen.",
      "Bij bunq en Revolut moet online gokken in de kaartinstellingen aanstaan.",
    ],
    bestFor: "Spelers met een Mastercard-debitkaart die snel willen storten en kunnen wachten op een kaartopname.",
    alternative: { slug: "visa", name: "Visa", why: "biedt vergelijkbare snelheid; bij sommige casino's werkt opnemen naar Visa net vaker." },
  },
  creditcard: {
    setup:
      "We testten met een Visa- en een Mastercard-creditcard van twee Nederlandse uitgevers, op desktop en mobiel. We letten extra op de afschrijvingsdatum op het creditcardoverzicht en op eventuele kosten die de uitgever als contantopname rekent.",
    deposit: [
      "Kaartbetaling kiezen en de creditcardgegevens invullen.",
      "Bevestigen met 3D Secure in de app van de kaartuitgever.",
      "Het saldo verscheen meestal binnen tien seconden; de afschrijving volgt pas op het maandoverzicht.",
    ],
    withdrawal: [
      "Opnemen naar dezelfde kaart waar het casino dat ondersteunt, anders per overschrijving naar je IBAN.",
      "We maten tot het bedrag op de rekening stond.",
    ],
    verification:
      "Casino's controleren het kaarttype bij de storting. Bij de eerste opname volgt een kaartfoto en ID.",
    pitfalls: [
      "Sommige uitgevers boeken een casinostorting als contantopname, met extra kosten en rente vanaf dag één.",
      "Uitgestelde afschrijving maakt het lastiger je budget te bewaken; stel een stortingslimiet in.",
    ],
    bestFor: "Spelers die al een creditcard gebruiken en de kosten en afschrijving van hun uitgever kennen.",
    alternative: { slug: "ideal", name: "iDEAL", why: "schrijft direct af van je eigen saldo, zonder rente of contantopnamekosten." },
  },
  klarna: {
    setup:
      "Klarna testten we via de directe-betaaloptie (Pay Now), die bij casino's de oude Sofort-route vervangt. Achteraf betalen is bij gokken niet toegestaan; dat controleerden we ook.",
    deposit: [
      "Klarna kiezen en je bank kiezen of inloggen in de Klarna-app.",
      "Bevestigen met de bank-app of TAN-code.",
      "Het saldo stond er meestal binnen een halve minuut.",
    ],
    withdrawal: [
      "Klarna betaalt niet uit; de winst gaat per overschrijving naar het IBAN van de storting.",
      "We maten van opnameverzoek tot bijschrijving.",
    ],
    verification:
      "De bankrekening is na de eerste Klarna-storting bekend bij het casino. ID bleef nodig bij de eerste opname.",
    pitfalls: [
      "Een optie 'achteraf betalen' bij een casino is een rood signaal.",
      "Klarna toont soms een Sofort-scherm; dat is dezelfde dienst.",
    ],
    bestFor: "Spelers die Klarna al kennen en een directe bankbetaling willen zonder iDEAL.",
    alternative: { slug: "ideal", name: "iDEAL", why: "doet hetzelfde met minder stappen voor Nederlandse rekeningen." },
  },
  "afterpay-casinos": {
    setup:
      "AfterPay heet tegenwoordig Riverty. We controleerden bij alle vergeleken aanbieders of achteraf betalen in de kassa stond. Dat was nergens het geval, en dat is in Nederland ook de bedoeling: gokken op krediet is niet toegestaan.",
    deposit: [
      "We zochten in elke kassa naar AfterPay of Riverty: niet gevonden.",
      "Als alternatief stortten we met iDEAL en Trustly, die direct van je eigen saldo afschrijven.",
    ],
    withdrawal: [
      "Er is geen AfterPay-opname; winst gaat altijd naar je eigen bankrekening.",
    ],
    verification:
      "Bij directe bankbetalingen is je rekening meteen bekend; dat maakt verificatie eenvoudiger dan bij een kredietroute.",
    pitfalls: [
      "Een site die wel achteraf betalen aanbiedt, werkt vrijwel zeker zonder Nederlandse vergunning.",
      "Speel alleen met geld dat je nu hebt; stel een stortingslimiet in.",
    ],
    bestFor: "Spelers die AfterPay of Riverty zoeken en willen weten wat het veilige alternatief is.",
    alternative: { slug: "ideal", name: "iDEAL", why: "schrijft direct af van je eigen rekening en is bij alle vergeleken aanbieders beschikbaar." },
  },
  muchbetter: {
    setup:
      "MuchBetter testten we met een geverifieerd account in de app op iPhone en Android, opgewaardeerd via bankoverschrijving.",
    deposit: [
      "MuchBetter kiezen en je telefoonnummer invullen.",
      "Een pushmelding in de app bevestigen.",
      "Het saldo stond er binnen ongeveer tien seconden.",
    ],
    withdrawal: [
      "Opnemen naar dezelfde MuchBetter-wallet.",
      "Na goedkeuring kwam het geld in onze tests binnen enkele uren binnen.",
    ],
    verification:
      "MuchBetter heeft eigen KYC. Casino's vroegen soms extra een screenshot van het account.",
    pitfalls: [
      "Doorboeken van de wallet naar je bank kan kosten met zich meebrengen.",
      "MuchBetter is bij minder aanbieders beschikbaar dan Skrill of Neteller.",
    ],
    bestFor: "Mobiele spelers die alles via één app met pushbevestiging willen regelen.",
    alternative: { slug: "skrill", name: "Skrill", why: "is breder beschikbaar en werkt vergelijkbaar als e-wallet." },
  },
  neosurf: {
    setup:
      "We kochten Neosurf-vouchers van €10 en €30 online en stortten daarmee op desktop en mobiel. Daarnaast bekeken we of een myNeosurf-account opnames mogelijk maakte.",
    deposit: [
      "Neosurf kiezen en het bedrag invullen.",
      "De 10-cijferige vouchercode invoeren.",
      "Bevestigen; het saldo verscheen binnen seconden.",
    ],
    withdrawal: [
      "Met een voucher kun je niet opnemen; winst gaat per overschrijving naar je IBAN.",
      "We maten de duur van die overschrijving tot bijschrijving.",
    ],
    verification:
      "Storten vraagt geen bankgegevens, maar voor de eerste opname moesten we altijd ID en een IBAN-bewijs aanleveren.",
    pitfalls: [
      "Een restsaldo op de voucher blijft staan tot je het gebruikt.",
      "Koop vouchers alleen bij officiële verkooppunten.",
    ],
    bestFor: "Spelers die een vast budget willen en bij het storten geen bankgegevens willen delen.",
    alternative: { slug: "paysafecard", name: "paysafecard", why: "is vergelijkbaar en bij meer winkels verkrijgbaar." },
  },
  telefoonrekening: {
    setup:
      "We testten Pay by Phone met twee Nederlandse telefoonabonnementen en één prepaid-kaart, op iPhone en Android. Elk account was nieuw, zodat we ook de sms-bevestiging en de eerste opname zonder bankverificatie meekregen.",
    deposit: [
      "In de kassa telefoonrekening of Pay by Phone kiezen en het bedrag invullen; de meeste aanbieders vragen vanaf €10.",
      "Het mobiele nummer invullen en de binnenkomende sms met bevestigingscode openen.",
      "De betaling bevestigen; bij prepaid verscheen de afschrijving direct op het beltegoed.",
      "Terug naar het casino: het saldo stond in alle tests tussen 38 en 68 seconden klaar.",
    ],
    withdrawal: [
      "Opnemen kan niet via de telefoonrekening; we vroegen de winst aan als bankoverschrijving naar een rekening op eigen naam.",
      "Bij de eerste opname vroeg elke aanbieder om een identiteitsbewijs en een uitbetaalrekening die bij het account hoorde.",
      "We meten bij elke opname opnieuw hoelang het duurt voordat het bedrag in de bank-app staat, niet wanneer het casino de status 'verwerkt' toont.",
    ],
    verification:
      "Storten vroeg alleen een telefoonnummer, maar bij de eerste opname controleerde de aanbieder naam, ID en IBAN. Wie bij registratie al met iDIN verifieerde, stond bij de opname merkbaar sneller buiten.",
    pitfalls: [
      "De dagcap van de provider geldt voor alle mobiele aankopen samen, dus een eerdere carrier-billingbetaling op de dag blokkeert de storting.",
      "Bevestig alleen een sms die hoort bij een storting die je zelf startte; een verlopen code hergebruiken lukt niet.",
    ],
    bestFor: "Mobiele spelers die zonder bankgegevens of extra app kleine bedragen willen storten en de dagcap van de provider als natuurlijke budgetgrens gebruiken.",
    alternative: { slug: "paysafecard", name: "paysafecard", why: "op hetzelfde prepaid-principe werkt, maar met een voucher zonder provider en zonder telefoonabonnement." },
  },
};

export function getMethodWalkthrough(slug: string): MethodWalkthrough | undefined {
  return METHOD_WALKTHROUGHS[slug];
}
